import {PullRequestDiffFileTree} from '@github-ui/diff-file-tree/pull-request-diff-file-tree'
import {File, type FileProps} from '@github-ui/diff-file-tree/pull-request-diff-file'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {clsx} from 'clsx'

import type {PullRequestFileTreeDiff} from '../../page-data/payloads/file-tree'
import {
  useActiveChangeGroupId,
  useChangeGroups,
  useLatestChangeGroupOrchestration,
  useShowChangeGroupsPreference,
  useChangeGroupGenerationUnavailableReason,
  useHasCommitRange,
  useThreadCount,
} from '../../stores/selectors'
import type {DiffDelta, FileNode} from '@github-ui/diff-file-tree/diff-file-tree-helpers'
import {memo, useCallback, useEffect, useMemo} from 'react'
import {GroupsList} from './GroupsList'
import {useChangeGroupsAnalytics} from '../../hooks/use-change-groups-analytics'
import type {DiffFilterState} from '../../stores/slices/diff-filter'
import {isCompletedChangeGroupState} from '../../helpers/change-groups'
import styles from './PullRequestFileTree.module.css'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export type PullRequestFileTreeProps = {
  className?: string
  filteredDiffs: PullRequestFileTreeDiff[] // filtered diff summaries
  onFileSelected?: () => void
  onSetFilterState: (updatedDiffFilterState: Partial<DiffFilterState>) => void
  fileFilter: React.ReactElement
  allowPullRequestGroups?: boolean
}

export const PR_FILE_TREE_ID = 'pr-file-tree'

/*
 * This component is intended to be visually and functionally consistent with the
 * @github-ui/commits FileTree component. Look for opportunities to upstream duplicated
 * code into the shared @github-ui/diff-file-tree package.
 */
export function PullRequestFileTree({
  className,
  fileFilter,
  filteredDiffs, // filtered diff summaries
  onFileSelected,
  onSetFilterState,
  allowPullRequestGroups = false,
}: PullRequestFileTreeProps) {
  const renderFile = useCallback((props: FileProps) => <PullRequestFile key={props.file.filePath} {...props} />, [])
  const changeGroups = useChangeGroups()
  const activeChangeGroupId = useActiveChangeGroupId()
  const hasCommitRange = useHasCommitRange()
  const changeGroupOrchestration = useLatestChangeGroupOrchestration()
  const showChangeGroupPreference = useShowChangeGroupsPreference()
  const {sendChangeGroupsPresentedEvent} = useChangeGroupsAnalytics()
  const isLoading = changeGroupOrchestration && !isCompletedChangeGroupState(changeGroupOrchestration.state)
  const unavailableReason = useChangeGroupGenerationUnavailableReason()
  const hideGroupsListForFewGroups = isFeatureEnabled('hide_groups_list_for_few_groups')

  // Hide GroupsList if unavailable reason is TOO_FEW_GROUPS or DIFF_TOO_SMALL or NOT_GENERATED_YET

  const shouldHideGroupsList =
    hideGroupsListForFewGroups &&
    (unavailableReason === 'TOO_FEW_GROUPS' ||
      unavailableReason === 'DIFF_TOO_SMALL' ||
      unavailableReason === 'NOT_GENERATED_YET')

  // Track when change groups would be presented but aren't due to user preference
  useEffect(() => {
    if (allowPullRequestGroups && changeGroups.length > 0 && !hasCommitRange && !showChangeGroupPreference) {
      sendChangeGroupsPresentedEvent({
        isErrorState: false,
        userPreferenceDisabled: true,
        hasGroups: true,
      })
    }
  }, [
    allowPullRequestGroups,
    hasCommitRange,
    showChangeGroupPreference,
    sendChangeGroupsPresentedEvent,
    changeGroups.length,
  ])

  return (
    <div className="d-flex flex-column height-fit">
      {allowPullRequestGroups && !hasCommitRange && showChangeGroupPreference && !shouldHideGroupsList && (
        <GroupsList
          activeGroupId={activeChangeGroupId}
          groups={changeGroups}
          onClearActiveGroup={() => {
            onSetFilterState({activeChangeGroupId: undefined})
          }}
          onSelectGroup={groupId => {
            onSetFilterState({activeChangeGroupId: groupId})
          }}
          isLoadingState={isLoading || false}
        />
      )}
      <ErrorBoundary boundaryName="PullRequestDiffFileTree" fallback={<span>File tree failed to load.</span>}>
        <div className={clsx('d-flex flex-column min-height-0', className)} id={PR_FILE_TREE_ID}>
          <div className={'tmp-pb-3 tmp-pr-lg-3'}>{fileFilter}</div>

          <div className={styles.FileTreeScrollable}>
            <h2 className="sr-only">File tree</h2>
            <PullRequestDiffFileTree
              diffs={filteredDiffs}
              fileNodeRenderer={renderFile}
              sortByDiffOrder
              onSelect={onFileSelected}
              className="tmp-pr-lg-3"
            />
          </div>
        </div>
      </ErrorBoundary>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
const PullRequestFile = memo(function PullRequestFile({file, ...props}: FileProps) {
  const threadIds = useMemo(() => {
    return Object.values((file.diff as PullRequestFileTreeDiff)?.markersMap ?? {}).flatMap(marker =>
      marker.threads.map(thread => thread.id),
    )
  }, [file.diff])

  const diffCommentCount = useThreadCount(threadIds)

  const fileWithComments: FileNode<DiffDelta> = useMemo(
    () => ({
      ...file,
      diff: {
        ...file.diff,
        totalCommentsCount: diffCommentCount,
      },
    }),
    [file, diffCommentCount],
  )

  return <File file={fileWithComments} {...props} />
})
